import { ReleaseNotesBundleService, FeatureFilePaths } from "../release-notes-bundle/release-notes-bundle";
import { FeatureIdentifier } from "../feature-config/feature-config";

export type FeatureReleaseNoteConfig = { [appId: string]: { [featureId: string]: Date } };
export type ReleaseNoteHtml = { splashHtml?: string; fullHtml?: string };
export type ReleaseNote = { featureId: FeatureIdentifier; bundleHtmls: ReleaseNoteHtml };
export type ReleaseNotesByAppId = { [appId: string]: ReleaseNote[] };

export class ReleaseNotesConfigService {
  private featureList: FeatureReleaseNoteConfig = {};

  // @ngInject
  constructor(private releaseNotesBundleService: ReleaseNotesBundleService, private $q: ng.IQService) {}

  public getFeatureList(): ng.IPromise<FeatureReleaseNoteConfig> {
    if (!this.isEmptyObject(this.featureList)) {
      return this.$q.resolve(this.featureList);
    }
    return this.releaseNotesBundleService.getAllFeatures().then((features: FeatureReleaseNoteConfig) => {
      this.featureList = features;
      return this.featureList;
    });
  }

  /**
   * Gets a list of feature identifiers that are released. Organized by app id.
   *
   * {
   *   "Knowledge": [ { featureId: "KNOWLEDGE_FEATURE", bundleHtmls: { ... } ],
   *   "Home": [
   *     { featureId: "HOME_FEATURE_1", bundleHtmls: { ... } },
   *     { featureId: "HOME_FEATURE_2", bundleHtmls: { ... } }
   *   ]
   * }
   *
   * @returns {ng.IPromise<ReleaseNotesByAppId>} promise of feature identifiers that are released.
   */
  public getReleasedFeatureIdentifiers(): ng.IPromise<ReleaseNotesByAppId> {
    return this.getFeatureList().then((features: FeatureReleaseNoteConfig) => {
      return this.getReleasedFeatures(features);
    });
  }

  public getReleaseNotesByFeatureList(releaseNotes: ReleaseNotesByAppId): ng.IPromise<ReleaseNotesByAppId> {
    const convertSplashPromiseAll: ng.IPromise<void>[] = [];
    Object.keys(releaseNotes).forEach((appId) => {
      releaseNotes[appId].forEach((releaseNote) => {
        const paths: FeatureFilePaths = this.releaseNotesBundleService.getFilePathsByFeatureIdentifier(
          releaseNote.featureId
        );
        convertSplashPromiseAll.push(
          this.releaseNotesBundleService.convertMdToHtml(paths.splashPath).then((splashHtml) => {
            releaseNote.bundleHtmls.splashHtml = splashHtml;
          })
        );
      });
    });
    return this.$q.all(convertSplashPromiseAll).then(() => {
      return releaseNotes;
    });
  }

  private getReleasedFeatures(features: FeatureReleaseNoteConfig): ng.IPromise<ReleaseNotesByAppId> {
    const releasedFeatures: ReleaseNotesByAppId = {};

    const appIds: string[] = Object.keys(features);

    const appIdsPromises = appIds.map((appId: string) => {
      releasedFeatures[appId] = [];
      return this.$q.all(
        Object.keys(features[appId]).map((featureId: FeatureIdentifier) => {
          const paths: FeatureFilePaths = this.releaseNotesBundleService.getFilePathsByFeatureIdentifier(featureId);

          // Convert splash
          const convertSplashPromise = this.releaseNotesBundleService.convertMdToHtml(paths.splashPath);

          // Convert full
          const convertFullPromise = this.releaseNotesBundleService.convertMdToHtml(paths.fullPath, paths.imagePaths);

          return this.$q.all([convertSplashPromise, convertFullPromise]).then(([splashHtml, fullHtml]) => {
            return { featureId, bundleHtmls: { splashHtml, fullHtml } };
          });
        })
      );
    });

    return this.$q.all(appIdsPromises).then((releasedNotesSortedByAppId: ReleaseNote[][]) => {
      const releasedFeatures: ReleaseNotesByAppId = {};

      for (let i = 0; i < releasedNotesSortedByAppId.length; i++) {
        releasedFeatures[appIds[i]] = releasedNotesSortedByAppId[i];
      }

      return releasedFeatures;
    });
  }

  private isEmptyObject(obj: any) {
    return Object.getOwnPropertyNames(obj).length === 0;
  }
}
